import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import styles from './CompanyTile.module.css';
import { FilterBar } from './FilterBar';
import { Favicon } from '../utils/favicon';
import GlobalEmptyState from './GlobalEmptyState';

interface Company {
  id: string;
  name: string;
  website?: string;
  // Legacy address fields
  address_street?: string;
  address_house_number?: string;
  address_zip_code?: string;
  address_city?: string;
  address_country?: string;
  // Legacy fields for backward compatibility
  number?: string;
}

interface CompanyTileProps {
  company: Company;
  onClick?: () => void;
}

export function CompanyTile({ company, onClick }: CompanyTileProps) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const { t } = useLocale();
  const [userCount, setUserCount] = useState<number>(0);
  const [loadingUserCount, setLoadingUserCount] = useState<boolean>(true);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Navigate to company detail page
      navigate(`/company/${company.id}`);
    }
  };

  const formatLocation = () => {
    // Use legacy address fields
    const city = company.address_city;
    const country = company.address_country;
    const zipCode = company.address_zip_code;

    const parts = [city, zipCode, country].filter(Boolean);
    return parts.join(', ');
  };

  // Fetch user count for this company
  useEffect(() => {
    const fetchUserCount = async () => {
      if (!company.id) {
        setUserCount(0);
        setLoadingUserCount(false);
        return;
      }

      try {
        setLoadingUserCount(true);
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('company', '==', company.id));
        const querySnapshot = await getDocs(q);
        setUserCount(querySnapshot.size);
      } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching user count for company:', error);
      }
        setUserCount(0);
      } finally {
        setLoadingUserCount(false);
      }
    };

    fetchUserCount();
  }, [company.id]);

  return (
    <div className={styles.companyTile} onClick={handleClick}>

      {/* Company icon */}
      <div className={styles.companyIcon}>
        {company.website ? (
          <Favicon
            websiteUrl={company.website}
            size={24}
            retinaMultiplier={4}
          />
        ) : (
          <img
            src={isDarkMode ? "/assets/icons/building.2.svg" : "/assets/icons/building.2.black.svg"}
            alt={t('company_tile_company_alt')}
            width={24}
            height={24}
          />
        )}
      </div>

      {/* Company information */}
      <div className={styles.companyInfo}>

        {/* Company name */}
        <h3 className={styles.companyTitle}>
          {company.name || t('company_tile_unknown_name')}
        </h3>

        {/* Company location */}
        <p className={styles.companyAddress}>
          {formatLocation() || t('company_tile_unknown_location')}
        </p>

      </div>

      {/* Status badge */}
      <div className={styles.statusContainer}>
        <div className={`${styles.statusBadge} ${userCount > 0 ? styles.statusUsers : styles.statusEmpty}`}>
          {loadingUserCount ? t('company_tile_loading_users') : `${userCount} ${t('company_tile_users_count')}`}
        </div>
      </div>
    </div>
  );
}

interface CompanyListProps {
  companies: Company[];
  loading: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onAddCompany?: () => void;
}

export function CompanyList({
  companies,
  loading,
  searchValue,
  onSearchChange,
  onAddCompany
}: CompanyListProps) {
  const { t } = useLocale();

  // Filter and search logic
  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      // Search filter - search by name or city
      const searchMatch = !searchValue ||
        company.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        (company.address_city && company.address_city.toLowerCase().includes(searchValue.toLowerCase()));

      return searchMatch;
    });
  }, [companies, searchValue]);

  return (
    <div className={styles.companyList}>
      {filteredCompanies.length > 0 && (
        <FilterBar
          filters={[]}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={t('company_tile_search_placeholder')}
          showInviteButton={false}
          showAddCompanyButton={!!onAddCompany}
          onAddCompany={onAddCompany}
        />
      )}

      {loading ? (
        <div className={styles.loadingContainer}>
          <p>{t('company_tile_loading_companies')}</p>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <GlobalEmptyState />
      ) : (
        filteredCompanies.map((company, index) => (
          <div key={company.id} className={styles.companyItem}>
            <CompanyTile
              company={company}
            />
          </div>
        ))
      )}
    </div>
  );
}

